import type { KeyboardEvent, ReactElement, ReactNode } from "react";
import { useRef } from "react";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/utils/cn";

// Shared WAI-ARIA combobox adapter (CLMS-125). clm-tracker-web's
// AutocompleteAdapter/CommandAdapter were deliberately not ported: they lack
// the WAI-ARIA combobox semantics this contract mandates (role="combobox",
// aria-activedescendant-driven active option, accurate aria-selected). This
// adapter implements the pattern directly, following the tracker-derived
// Input/Select conventions in this folder: shared Input for the text field and
// the Select popover surface tokens (bg-popover, border, shadow-md) for the
// listbox. DOM focus stays on the input; the active option is conveyed
// exclusively through aria-activedescendant, so the popup can live inline (no
// portal). Inside a scrollable DialogContent the inline popup contributes to
// the dialog's scroll height rather than escaping it: every option stays
// reachable by scrolling, and keyboard navigation auto-scrolls the active
// option into view.

export interface ComboboxOption {
  /** Stable DOM id for the option element, referenced by aria-activedescendant. */
  id: string;
}

export interface ComboboxValueChangeMeta {
  /** True while the value reflects uncommitted IME composition text. */
  isComposing: boolean;
}

export interface ComboboxProps<T extends ComboboxOption> {
  inputId: string;
  listboxId: string;
  /** Accessible name for the result listbox. */
  listboxLabel: string;
  value: string;
  /**
   * Fired for every input value change. `meta.isComposing` is true for IME
   * composition updates, which are uncommitted input, and once on
   * compositionstart with the unchanged current value, so a consumer learns a
   * composition has begun before its first change; the final composed value
   * is committed exactly once (isComposing false) on compositionend.
   */
  onValueChange: (value: string, meta: ComboboxValueChangeMeta) => void;
  options: readonly T[];
  /** Popup visibility; fully controlled by the parent. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** aria-activedescendant target; null when no option is active. */
  activeOptionId: string | null;
  onActiveOptionIdChange: (id: string | null) => void;
  /** The explicitly selected option, reflected through aria-selected. */
  selectedOptionId?: string | null;
  onOptionSelect: (option: T) => void;
  renderOption: (option: T, state: { active: boolean; selected: boolean }) => ReactNode;
  placeholder?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
  /** Programmatic required state; the visible marker alone is not exposed to AT. */
  "aria-required"?: boolean;
}

export function Combobox<T extends ComboboxOption>({
  inputId,
  listboxId,
  listboxLabel,
  value,
  onValueChange,
  options,
  open,
  onOpenChange,
  activeOptionId,
  onActiveOptionIdChange,
  selectedOptionId,
  onOptionSelect,
  renderOption,
  placeholder,
  disabled,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-required": ariaRequired,
}: ComboboxProps<T>): ReactElement {
  const expanded = open && options.length > 0;
  const activeIndex = options.findIndex((option) => option.id === activeOptionId);
  // jsdom does not surface InputEvent.isComposing, so composition state is
  // tracked from the composition events themselves.
  const composingRef = useRef(false);

  const activateOption = (option: T): void => {
    onActiveOptionIdChange(option.id);
    // jsdom has no scrollIntoView; real browsers keep the option visible.
    document.getElementById(option.id)?.scrollIntoView?.({ block: "nearest" });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    // Keys pressed during IME composition operate on uncommitted text; they
    // must never navigate options, select, or clear. isComposing alone is not
    // enough: Chrome reports the composition-commit key as keyCode 229, and
    // Safari can fire a trailing Enter keydown with isComposing false while
    // composition is still being committed — composingRef (maintained from
    // the composition events) and keyCode 229 close both holes.
    if (composingRef.current || event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        if (options.length === 0) {
          return;
        }
        if (!expanded) {
          onOpenChange(true);
          activateOption(options[0]);
          return;
        }
        // No active option (activeIndex -1): enter the list at the first
        // option explicitly rather than relying on modulo arithmetic.
        if (activeIndex < 0) {
          activateOption(options[0]);
          return;
        }
        activateOption(options[(activeIndex + 1) % options.length]);
        return;
      }
      case "ArrowUp": {
        event.preventDefault();
        if (options.length === 0) {
          return;
        }
        if (!expanded) {
          onOpenChange(true);
          activateOption(options[options.length - 1]);
          return;
        }
        // No active option (activeIndex -1): ArrowUp enters the list at the
        // last option; modulo from -1 would land on the second-to-last.
        if (activeIndex < 0) {
          activateOption(options[options.length - 1]);
          return;
        }
        activateOption(options[(activeIndex - 1 + options.length) % options.length]);
        return;
      }
      case "Home": {
        if (expanded) {
          event.preventDefault();
          activateOption(options[0]);
        }
        return;
      }
      case "End": {
        if (expanded) {
          event.preventDefault();
          activateOption(options[options.length - 1]);
        }
        return;
      }
      case "Enter": {
        // Enter never submits an enclosing form from the combobox: it either
        // selects the active option or is swallowed (search is not a mutation).
        event.preventDefault();
        if (expanded && activeIndex >= 0) {
          onOptionSelect(options[activeIndex]);
        }
        return;
      }
      case "Escape": {
        // The enclosing dialog (if any) is responsible for not dismissing on
        // this Escape (see the grant dialog's onEscapeKeyDown); here the
        // popup closes first, then a second Escape clears the text.
        if (expanded) {
          event.preventDefault();
          event.stopPropagation();
          onOpenChange(false);
          onActiveOptionIdChange(null);
          return;
        }
        if (value.length > 0) {
          event.preventDefault();
          event.stopPropagation();
          onValueChange("", { isComposing: false });
        }
        return;
      }
      default:
    }
  };

  return (
    <div className="relative">
      <Input
        id={inputId}
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={expanded}
        // ARIA 1.2: aria-controls only while the popup is present; a dangling
        // IDREF to an unrendered listbox is mishandled by some AT.
        aria-controls={expanded ? listboxId : undefined}
        aria-activedescendant={
          expanded && activeIndex >= 0 ? (activeOptionId ?? undefined) : undefined
        }
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) =>
          onValueChange(event.target.value, { isComposing: composingRef.current })
        }
        onCompositionStart={(event) => {
          composingRef.current = true;
          // Report the start at once so a consumer can stop acting on earlier
          // committed text before the first composing change arrives.
          onValueChange(event.currentTarget.value, { isComposing: true });
        }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          // Commit exactly one value change from the final composed text.
          onValueChange(event.currentTarget.value, { isComposing: false });
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => onOpenChange(false)}
      />
      {expanded && (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={listboxLabel}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover py-1 text-popover-foreground shadow-md"
        >
          {options.map((option) => {
            const active = option.id === activeOptionId;
            const selected = option.id === selectedOptionId;
            return (
              <li
                key={option.id}
                id={option.id}
                role="option"
                aria-selected={selected}
                className={cn(
                  "cursor-pointer px-3 py-2 hover:bg-accent/20",
                  active && "bg-primary text-primary-foreground hover:bg-primary",
                )}
                // preventDefault keeps DOM focus on the combobox input so the
                // popup does not close before the click selects the option.
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => {
                  if (!active) {
                    onActiveOptionIdChange(option.id);
                  }
                }}
                onClick={() => onOptionSelect(option)}
              >
                {renderOption(option, { active, selected })}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
